// WHERE THE TIME GOES BEFORE SAVE AND SUBMIT.
//
// Kevin, 2026-10-16: "reduce the time from when cpq2 is opened to the time it is submitted as
// much as possible ... people say its a slow process." cpq2_timing already timed the dataset
// load and the save, and between them they are a few seconds. The rows grouped by
// lifecycle_id put a NEW Natural Draft configuration at a median of 6.8 minutes from load to
// save -- so nearly all of the time is spent in the form, and nothing measured any of it.
//
// Two rows, both through recordLifecycle (sendBeacon, fire and forget):
//
//   'ready'              navigation start -> the form is usable (saved config restored, for
//                        Edit Config). Split into: until a configurator was chosen (includes
//                        the home-page pick on Configure), the dataset, the restore.
//   'configure-session'  ready -> the Save and Submit click that went through, or 'abandoned'
//                        when the tab closes first. Time per tab, answer changes, Calculate
//                        clicks, blocked submits and the fields they flagged, and IDLE time:
//                        gaps over a minute with no input, which separate "the form is slow to
//                        work through" from "the user was looking something up".
//
// RULE 1, as in lifecycle.ts: measurement never throws and never changes control flow.
import { recordLifecycle } from "./lifecycle";

const now = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();

/** A gap in input longer than this counts as idle -- away from the form, not working in it. */
const IDLE_GAP_MS = 60_000;

type State = {
  product: string | null;
  configMode: string | null;
  chosenAt: number | null; // a configurator was decided on (picker click, or the item lookup)
  datasetAt: number | null; // its dataset finished loading
  readyAt: number | null;
  tab: string | null;
  tabSince: number;
  tabMs: Record<string, number>;
  tabSwitches: number;
  answerChanges: number;
  fields: Set<string>;
  firstAnswerAt: number | null;
  lastInputAt: number | null;
  idleMs: number;
  events: Record<string, number>;
  blocked: Array<{ kind: string; count: number; at: number }>;
  flagged: Record<string, number>;
  done: boolean;
  listening: boolean;
};

const s: State = {
  product: null, configMode: null, chosenAt: null, datasetAt: null, readyAt: null,
  tab: null, tabSince: 0, tabMs: {}, tabSwitches: 0,
  answerChanges: 0, fields: new Set(), firstAnswerAt: null, lastInputAt: null, idleMs: 0,
  events: {}, blocked: [], flagged: {}, done: false, listening: false,
};

const safe = (fn: () => void) => { try { fn(); } catch { /* rule 1 */ } };

function input(): void {
  const t = now();
  if (s.readyAt != null && s.lastInputAt != null && t - s.lastInputAt > IDLE_GAP_MS) {
    s.idleMs += t - s.lastInputAt;
  }
  s.lastInputAt = t;
}

function closeTab(t: number): void {
  if (s.tab) s.tabMs[s.tab] = (s.tabMs[s.tab] ?? 0) + Math.max(0, t - s.tabSince);
  s.tabSince = t;
}

function listen(): void {
  if (s.listening || typeof window === "undefined") return;
  s.listening = true;
  // Any key or click is input, whether or not it changes an answer (scrolling a table, opening
  // a dropdown). Passive and capture-phase so it can never interfere with the form.
  window.addEventListener("pointerdown", () => safe(input), { passive: true, capture: true });
  window.addEventListener("keydown", () => safe(input), { passive: true, capture: true });
  // A session that never reached a submit is the one most worth having: it may be the user
  // who gave up because it was slow.
  window.addEventListener("pagehide", () => safe(() => report("abandoned")));
}

/** The configurator is decided (Configure: the home-page pick; Edit Config: the item lookup). */
export function markChosen(product: string): void {
  safe(() => { s.product = product; if (s.chosenAt == null) s.chosenAt = now(); });
}

/** Its dataset has loaded. */
export function markDataset(): void {
  safe(() => { if (s.datasetAt == null) s.datasetAt = now(); });
}

/** The form is usable. Records 'ready' once per page load. */
export function markReady(opts: { product: string; configMode: string; tab: string | null }): void {
  safe(() => {
    if (s.readyAt != null) return;
    const t = now();
    s.readyAt = t;
    s.product = opts.product;
    s.configMode = opts.configMode;
    s.tab = opts.tab;
    s.tabSince = t;
    s.lastInputAt = t;
    listen();
    const steps: Array<{ name: string; ms: number }> = [];
    if (s.chosenAt != null) steps.push({ name: "until a configurator was chosen", ms: Math.round(s.chosenAt) });
    if (s.chosenAt != null && s.datasetAt != null) steps.push({ name: "dataset load", ms: Math.round(s.datasetAt - s.chosenAt) });
    if (s.datasetAt != null) steps.push({ name: "form ready (incl. restoring a saved config)", ms: Math.round(t - s.datasetAt) });
    recordLifecycle({
      lifecycle: "ready", outcome: "ok", totalMs: t, product: opts.product, configMode: opts.configMode, steps,
    });
  });
}

export function tabChanged(tab: string | null): void {
  safe(() => {
    if (s.readyAt == null || tab === s.tab) return;
    closeTab(now());
    s.tab = tab;
    s.tabSwitches++;
  });
}

/** A user-made answer change (AnswersContext.setAnswer), not an operation's. */
export function answerChanged(qCode: string): void {
  safe(() => {
    if (s.readyAt == null) return;
    s.answerChanges++;
    s.fields.add(qCode);
    if (s.firstAnswerAt == null) s.firstAnswerAt = now();
    input();
  });
}

/** Calculate, the Next: jump, a Favorite or Item Template load... */
export function noteEvent(name: string): void {
  safe(() => { s.events[name] = (s.events[name] ?? 0) + 1; });
}

/** Save and Submit refused to go: which check, and the fields it pointed at. */
export function noteBlockedSubmit(kind: string, fieldCodes: string[]): void {
  safe(() => {
    const t = s.readyAt == null ? 0 : now() - s.readyAt;
    s.blocked.push({ kind, count: fieldCodes.length, at: Math.round(t) });
    for (const c of fieldCodes) s.flagged[c] = (s.flagged[c] ?? 0) + 1;
  });
}

/** The Save and Submit click that actually went through. */
export function noteSubmitted(): void {
  safe(() => report("ok"));
}

function report(outcome: "ok" | "abandoned"): void {
  if (s.done || s.readyAt == null) return;
  s.done = true;
  const t = now();
  closeTab(t);
  // A trailing gap counts too: a tab left open for an hour and then closed was not an hour of
  // configuring.
  if (s.lastInputAt != null && t - s.lastInputAt > IDLE_GAP_MS) s.idleMs += t - s.lastInputAt;
  const total = t - s.readyAt;
  const steps = Object.entries(s.tabMs)
    .sort((a, b) => b[1] - a[1])
    .map(([name, ms]) => ({ name: `tab: ${name}`, ms: Math.round(ms) }));
  recordLifecycle({
    lifecycle: "configure-session",
    outcome,
    totalMs: total,
    product: s.product,
    configMode: s.configMode,
    reason: outcome === "abandoned" ? "tab closed before a submit went through" : null,
    steps,
    context: {
      activeMs: Math.round(Math.max(0, total - s.idleMs)),
      idleMs: Math.round(s.idleMs),
      toFirstAnswerMs: s.firstAnswerAt == null ? null : Math.round(s.firstAnswerAt - s.readyAt),
      answerChanges: s.answerChanges,
      fieldsTouched: s.fields.size,
      // WHICH fields people set by hand -- the snapshots cannot tell a hand-set value from one
      // an operation wrote, and that is the list a better default or a "start from the last
      // configuration" has to beat.
      touched: [...s.fields].slice(0, 120),
      tabSwitches: s.tabSwitches,
      events: s.events,
      blockedSubmits: s.blocked.length,
      blocked: s.blocked.slice(0, 20),
      // The fields Save and Submit most often sent people back for.
      flagged: Object.entries(s.flagged).sort((a, b) => b[1] - a[1]).slice(0, 25),
    },
  });
}
